/**
 * Phase 7 Slice D.2 — Notification Bell.
 *
 * Topbar dropdown listing the last 20 in-app notifications for the
 * current user (plus tenant-wide rows where ``user_id IS NULL``).
 * Unread count badge updates on a WS ``notification`` envelope and
 * after the user opens the dropdown / clicks "mark all read".
 *
 * Tenant-wide rows render with a small "Tenant" badge so operators can
 * distinguish broadcast events (DLQ alerts, scheduler errors) from
 * personal events.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellDot, Check } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

function fmtRelative(iso, locale = "de") {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return locale === "de" ? "gerade eben" : "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

const SEVERITY_COLORS = {
  critical: "border-rose-200 bg-rose-50 text-rose-900",
  warning: "border-amber-200 bg-amber-50 text-amber-900",
  info: "border-slate-200 bg-white text-slate-800",
};

export default function NotificationBell({ wsTick = 0 }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { limit: 20 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch (e) { /* ignore — bell is best-effort */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Refresh whenever a WS notification arrives (parent bumps wsTick).
  useEffect(() => { if (wsTick > 0) load(); }, [wsTick, load]);

  // Refresh the list when the dropdown opens.
  useEffect(() => { if (open) load(); }, [open, load]);

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setUnread(0);
      setItems((prev) => prev.map((n) => ({
        ...n, read_at: n.read_at || new Date().toISOString(),
      })));
    } catch (e) { /* ignore */ }
  };

  const Icon = unread > 0 ? BellDot : Bell;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm"
                  data-testid="notification-bell" className="relative gap-1">
          <Icon className="size-4" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1
                            rounded-full bg-rose-600 text-white text-[10px] font-bold
                            flex items-center justify-center"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[380px] p-0"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between border-b px-4 py-2">
          <div className="text-sm font-semibold">
            {t("notifications.title", "Benachrichtigungen")}
          </div>
          <button
            type="button"
            onClick={markAll}
            disabled={unread === 0}
            data-testid="notification-mark-all-read"
            className="text-xs text-muted-foreground hover:text-foreground
                          disabled:opacity-40 disabled:cursor-not-allowed
                          inline-flex items-center gap-1"
          >
            <Check className="h-3 w-3" />
            {t("notifications.mark_all_read", "Alle als gelesen markieren")}
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className="px-4 py-6 text-xs text-muted-foreground"
                 data-testid="notification-loading">
              {t("common.loading", "Lädt…")}
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground"
                 data-testid="notification-empty">
              {t("notifications.empty", "Noch keine Benachrichtigungen.")}
            </div>
          ) : items.map((n) => {
            const sev = (n.severity || "info").toLowerCase();
            const cls = SEVERITY_COLORS[sev] || SEVERITY_COLORS.info;
            const unreadRow = !n.read_at;
            return (
              <div
                key={n.id}
                data-testid={`notification-row-${n.id}`}
                className={`px-4 py-3 border-b last:border-0 ${cls}
                                ${unreadRow ? "font-medium" : "opacity-80"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm leading-tight flex-1 min-w-0">
                    <span data-testid={`notification-subject-${n.id}`}>
                      {n.subject}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!n.user_id && (
                      <Badge
                        variant="outline"
                        className="text-[10px] py-0 px-1.5"
                        data-testid={`notification-tenant-badge-${n.id}`}
                      >
                        {t("notifications.tenant_badge", "Tenant")}
                      </Badge>
                    )}
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {fmtRelative(n.created_at)}
                    </span>
                  </div>
                </div>
                {n.body_markdown && (
                  <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                    {n.body_markdown}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
